/**
 * In-place playback of a cited audio span, for FR-C9 source cards (#1413).
 *
 * "Plays only the cited span's audio, without leaving the drawer" is the
 * acceptance criterion, and the two halves pull in different directions: the
 * card must start audio, and the drawer must not become an audio player. So
 * this owns exactly one `HTMLAudioElement` for the whole app and nothing else
 * — no transport, no scrubber, no duration readout.
 *
 * ONE element, not one per card, and that is the load-bearing decision. A card
 * that minted its own element would leave three sources playing over each
 * other on a three-source answer, and each would keep playing after the
 * message scrolled away. Starting a span always stops whatever was playing.
 *
 * Audio is fetched from the same-origin Range proxy at
 * `/api/transcripts/{id}/audio/stream`, which exists because the converted
 * audio bucket sits inside a VPC-SC perimeter and a browser cannot fetch a
 * signed GCS URL directly (it gets 403 SecurityPolicyViolated). The proxy
 * authenticates from the `__session` cookie, so a plain `src` works and no
 * Authorization header is needed.
 *
 * The proxy DOES require `tenant_id` in the query string and answers 400
 * without it (#1613). That is why the tenant is a required field on
 * {@link PlayableSpan} rather than something left to the caller: this shipped
 * once without it, and because a rejected `play()` resets the control quietly,
 * every card looked clickable and none of them made a sound.
 */

/** Identifies a playing span. `0` is a real start, so this is not falsy-safe. */
export function spanKey(transcriptId: string, startTs: number | null): string {
  return `${transcriptId}:${startTs ?? 'none'}`;
}

export function spanAudioUrl(transcriptId: string, tenantId: string): string {
  return `/api/transcripts/${encodeURIComponent(transcriptId)}/audio/stream?tenant_id=${encodeURIComponent(tenantId)}`;
}

export interface PlayableSpan {
  transcriptId: string;
  /** Active tenant. The stream proxy rejects a request without it (#1613). */
  tenantId: string;
  /** Seconds. `0` is a real opening span — callers must gate on `!== null`. */
  startTs: number | null;
  /** Seconds. Null means "play on from the start point" rather than "no span". */
  endTs: number | null;
}

type Listener = () => void;
type AudioFactory = (src: string) => HTMLAudioElement;

const defaultAudioFactory: AudioFactory = (src) => new Audio(src);

export class SpanPlaybackController {
  private audio: HTMLAudioElement | null = null;
  private currentKey: string | null = null;
  private stopAt: number | null = null;
  private readonly listeners = new Set<Listener>();

  constructor(private readonly createAudio: AudioFactory = defaultAudioFactory) {}

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * The key of the span currently playing, or null.
   *
   * Returns a string or null rather than an object so `useSyncExternalStore`
   * can compare snapshots by identity — a fresh object every call would
   * re-render forever.
   */
  getSnapshot = (): string | null => this.currentKey;

  /** Server render has no audio; snapshots must agree or React warns. */
  getServerSnapshot = (): string | null => null;

  private emit(): void {
    for (const listener of this.listeners) listener();
  }

  private handleTimeUpdate = (): void => {
    if (!this.audio || this.stopAt === null) return;
    if (this.audio.currentTime >= this.stopAt) this.stop();
  };

  private handleEnded = (): void => {
    this.stop();
  };

  private detach(): void {
    if (!this.audio) return;
    this.audio.removeEventListener('timeupdate', this.handleTimeUpdate);
    this.audio.removeEventListener('ended', this.handleEnded);
    this.audio.pause();
    this.audio = null;
  }

  /**
   * Play one span. Toggles: calling with the span already playing stops it,
   * which is what a single play/pause control on a card has to do.
   *
   * A null `startTs` is rejected rather than treated as 0. The card is
   * supposed to render no control at all in that case (AC), so reaching here
   * means a caller lost the guard — starting the file from the top would
   * present unrelated audio as the cited evidence.
   */
  play(span: PlayableSpan): void {
    if (span.startTs === null) return;
    // No tenant resolved (the tenant context's empty-string sentinel). The
    // proxy would 400, and starting an element that is certain to fail only
    // flashes the control to "playing" and back.
    if (!span.tenantId) return;

    const key = spanKey(span.transcriptId, span.startTs);
    if (this.currentKey === key) {
      this.stop();
      return;
    }

    this.detach();

    const audio = this.createAudio(spanAudioUrl(span.transcriptId, span.tenantId));
    this.audio = audio;
    this.currentKey = key;
    // Null endTs means "play on" rather than "no span": the backend writes
    // both bounds or neither, so a lone start is a partial row, and playing
    // from the right place beats refusing.
    this.stopAt = span.endTs !== null && span.endTs > span.startTs ? span.endTs : null;

    audio.addEventListener('timeupdate', this.handleTimeUpdate);
    audio.addEventListener('ended', this.handleEnded);

    // Seeking before the element has metadata is legal — the browser applies
    // it once seekable. Assigning after `play()` would start at 0 and jump,
    // audibly playing a moment of the wrong span.
    try {
      audio.currentTime = span.startTs;
    } catch {
      // Some engines throw until metadata arrives; the load handler below
      // re-applies it rather than leaving playback at the file start.
      audio.addEventListener(
        'loadedmetadata',
        () => {
          if (this.audio === audio) audio.currentTime = span.startTs as number;
        },
        { once: true }
      );
    }

    // `play()` rejects on autoplay policy or a failed fetch. Swallowing it
    // would leave the card stuck showing "playing" forever, so reset state.
    const started = audio.play() as unknown as Promise<void> | undefined;
    if (started && typeof started.catch === 'function') {
      started.catch(() => {
        if (this.currentKey === key) this.stop();
      });
    }

    // #1619: only one audio source may sound at a time across the app. Taking
    // over pauses the transcript page's sticky player via the existing
    // `pause-all-audio` bus (dispatched by Sidebar nav, listened for by the
    // transcript detail page). We only DISPATCH it — this controller does not
    // listen, so there is no self-stop loop. The reverse direction (the sticky
    // player stopping span playback) is wired on the transcript page's own
    // `play` event. Guarded for SSR / the non-DOM unit-test env.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('pause-all-audio'));
    }

    this.emit();
  }

  stop(): void {
    this.detach();
    this.currentKey = null;
    this.stopAt = null;
    this.emit();
  }
}

/** App-wide singleton. Tests construct their own with a fake element. */
export const spanPlayback = new SpanPlaybackController();
